import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  FlashbarProps,
  Pagination,
  Table,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';

import {
  getDataSourceTables,
  registerDataSets as catalogRegisterDataSets,
  syncDataSets,
} from '../../../src/api/catalog';
import { compareBy } from '../../../src/components/utils/sorting';
import { getDataAccessConfig } from '../../../src/api/config';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { createCatalogDetailLink, createDatabaseDetailLink } from 'src/routes';
import { LAKE_FORMATION_DATASOURCE_ID, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { EmptyState } from 'src/commons/EmptyState';
import { CommonPageProps } from '../common/CommonPageProps';

import { PageHeader } from 'src/components/common/PageHeader';
import {
  GetDataSourceTablesRequest,
  GetDataSourceTablesResponse,
  RegisterDataSetRequest,
  RegisterDataSetStructure,
  RegisterDataSetStructureList,
} from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';

export interface RegisterTableProps extends CommonPageProps {
  onNotificationChange?: (notifications: FlashbarProps.MessageDefinition[]) => void;
  idFilter?: string[];
  databaseDetail: object;
  extraFeatures: boolean;
  onlyShowDatasetName: boolean;
  title: string;
  loading?: boolean; // This is unused.
  activeGroup?: string;
}

const RegisterTable = (props: RegisterTableProps) => {
  const [dataSourceTables, setDataSourceTables] = useState([]);
  const [registeredItems, setItems] = useState(['cloudtrailtest_cloudtrail']);

  const [dataSetsFromDataSourceLoading, setDataSetsFromDataSourceLoading] = useState(true);
  const [registerDataSetsLoading, setRegisterDataSetsLoading] = useState(false);
  const [dataAccessDetails, setDataAccessDetails] = useState({});
  const [notifications, setNotifications] = useState([]);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const [columnDefinitions, setColumnDefinitions] = useState<TableProps.ColumnDefinition<any>[]>([
    {
      id: 'catalogId',
      header: 'Catalog ID',
      cell: () => (
        <Link
          href={createCatalogDetailLink(
            props.databaseDetail['DataSourceId'],
            props.databaseDetail['CatalogId'],
            props.databaseDetail['ClusterId'],
            props.databaseDetail['RedshiftWorkgroupName'],
            props.databaseDetail['BucketName'],
            props.databaseDetail['Region'],
          )}
        >
          {props.databaseDetail['CatalogId']}
        </Link>
      ),
      minWidth: 100,
      sortingComparator: compareBy('catalogId', 'databaseName', 'tableName'),
    },
    {
      id: 'databaseName',
      header: 'Database name',
      cell: () => (
        <Link
          href={createDatabaseDetailLink(
            props.databaseDetail['DataSourceId'],
            props.databaseDetail['CatalogId'],
            undefined,
            undefined,
            props.databaseDetail['BucketName'],
            props.databaseDetail['DatabaseName'],
            props.databaseDetail['Region'],
          )}
        >
          {props.databaseDetail['DatabaseName']}
        </Link>
      ),
      minWidth: 200,
      sortingComparator: compareBy('databaseName', 'tableName'),
    },
    {
      id: 'tableName',
      header: 'Table name',
      cell: (item) => item.Name,
      minWidth: 200,
      sortingField: 'Name,',
    },
  ]);

  useEffect(() => {
    if (props.onlyShowDatasetName) {
      setColumnDefinitions([
        {
          id: 'dataSetName',
          header: 'Table name',
          cell: (item) => item.Name,
          minWidth: 200,
        },
      ]);
    }
    setDataAccessDetails(getDataAccessConfig());

    props.setContentType(TABLE_CONTENT_TYPE);
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [props.idFilter, props.databaseDetail]);

  // Fetch datasets to register and tables names from registered tables.
  const fetchAllData = async () => {
    getTableNamesFromIdFilter();
    await fetchDataSetsFromDataSource();
  };

  // Gets all the Tables from the Data Source
  const fetchDataSetsFromDataSource = async () => {
    setDataSetsFromDataSourceLoading(true);
    let getDataSourceTablesRequest: GetDataSourceTablesRequest = {
      DataSourceId: LAKE_FORMATION_DATASOURCE_ID,
      CatalogId: props.databaseDetail['CatalogId'],
      DatabaseName: props.databaseDetail['DatabaseName'],
      Region: props.databaseDetail['Region'],
      NextToken: null,
    };
    try {
      let tableList = [];
      let getDataSourceTablesResponse: GetDataSourceTablesResponse = await getDataSourceTables(
        getDataSourceTablesRequest,
      );
      tableList = getDataSourceTablesResponse.TableList;
      while (getDataSourceTablesResponse.NextToken != null) {
        getDataSourceTablesRequest.NextToken = getDataSourceTablesResponse.NextToken;
        getDataSourceTablesResponse = await getDataSourceTables(getDataSourceTablesRequest);
        tableList.push(...getDataSourceTablesResponse.TableList);
      }
      setDataSourceTables(tableList);
    } catch (e) {
      console.log('Error getting tables from: ', props.databaseDetail['DatabaseName'], e.message);
    }

    setDataSetsFromDataSourceLoading(false);
  };

  // Reuse the Id filter and get registered table names.
  const getTableNamesFromIdFilter = () => {
    if (props.idFilter) {
      setItems(props.idFilter.map((value) => value.split('|')[3].replace(/TN-/i, '')));
    }
  };

  const registerDataSets = async () => {
    setRegisterDataSetsLoading(true);
    const dataSetList = createDataSetList();
    const registerRequest: RegisterDataSetRequest = {
      CreatedBy: props.activeGroup,
      DataSourceId: LAKE_FORMATION_DATASOURCE_ID,
      CatalogId: props.databaseDetail['CatalogId'],
      Region: props.databaseDetail['Region'],
      DataAccessRole: dataAccessDetails['dataAccessRoleArn'],
      DataSetList: dataSetList,
    };

    // if register step fails, do not move on to sync step
    let registerResponse;
    try {
      registerResponse = await catalogRegisterDataSets(registerRequest);
    } catch (err) {
      props.onNotificationChange([
        {
          type: 'error' as FlashbarProps.Type,
          content: `Failed to register datasets.`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      return;
    }

    // now sync datasets' metadata with glue (or other data sources in the future)
    const idList = registerResponse.DataSetList.map((e) => e.Id);
    const syncRequest = {
      IdList: idList,
    };

    try {
      await syncDataSets(syncRequest);
    } catch (err) {
      props.onNotificationChange([
        {
          type: 'error' as FlashbarProps.Type,
          content: `Failed to sync datasets with source.`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      return;
    }

    props.onNotificationChange([
      {
        type: 'success' as FlashbarProps.Type,
        content: `Successfully registered and synced.`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);

    setRegisterDataSetsLoading(false);
  };

  const createDataSetList = () => {
    return collectionProps.selectedItems.map(
      (item) =>
        ({
          DatabaseName: props.databaseDetail['DatabaseName'],
          TableName: item['Name'],
          DataSetName: item['Name'],
          PrimaryOwner: props.activeGroup,
          TableOwners: [props.activeGroup],
          DataClassification: 'Confidential',
          TableState: 'Active',
        } as RegisterDataSetStructure),
    ) as RegisterDataSetStructureList;
  };

  const { items, collectionProps, paginationProps } = useCollection(dataSourceTables, {
    filtering: {},
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  return (
    <>
      <Flashbar items={notifications} />
      <Table
        {...collectionProps}
        selectionType='multi'
        isItemDisabled={(item) => registeredItems.includes(item['Name'])}
        loadingText='Loading datasets...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={
          <PageHeader
            buttons={
              props.extraFeatures
                ? [
                    {
                      text: 'Register',
                      variant: 'normal',
                      disabled: collectionProps.selectedItems.length === 0,
                      loading: registerDataSetsLoading,
                      onItemClick: (_) => {
                        registerDataSets();
                      },
                    },
                  ]
                : []
            }
            counter={`(${items.length})`}
            header={props.title}
          />
        }
        empty={<EmptyState title='No datasets' subtitle='No datasets were found for this database.' />}
        loading={dataSetsFromDataSourceLoading}
        pagination={props.extraFeatures && <Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          props.extraFeatures && (
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={largePageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          )
        }
      />
    </>
  );
};

export default RegisterTable;
